import { FieldError, FieldErrors, RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WidgetInputs } from '../@types/types';

export enum FieldErrorType {
	REQUIRED = 'required',
	MIN = 'min',
	MAX = 'max',
	MAX_LENGTH = 'maxLength',
	MIN_LENGTH = 'minLength',
}

const getFieldError = (type: string, name: keyof WidgetInputs) => {
	if (type === FieldErrorType.REQUIRED) {
		switch (name) {
			case 'Amount':
				return 'errors.requiredAmount';
			case 'Recurrence':
			case 'GiftDate':
			case 'RecurringEndOccurence':
			case 'FundKeyOrName':
			case 'RecurringStartDate':
			case 'RecurringEndType':
				return 'errors.required';
			case 'RecurringEndDate':
				return 'errors.validEndDateRequired';
			default:
				return 'errors.required';
		}
	}

	if (type === FieldErrorType.MIN) {
		switch (name) {
			case 'Amount':
				return 'errors.amountMinMax';
			case 'GiftDate':
			case 'RecurringStartDate':
				return 'errors.dateInPast';
			case 'RecurringEndDate':
				return 'errors.endAfterStart';
			default:
				return 'errors.invalid';
		}
	}

	if (type === FieldErrorType.MIN_LENGTH) {
		return 'errors.minRequired';
	}

	if (type === FieldErrorType.MAX_LENGTH) {
		return 'errors.maxRequired';
	}

	if (type === FieldErrorType.MAX) {
		switch (name) {
			case 'Amount':
				return 'errors.amountMinMax';
			case 'RecurringEndOccurence':
				return 'errors.occurenceMinMax';
			default:
				return 'errors.maxRequired';
		}
	}
	return 'errors.invalid';
};

export function useErrorMessageOld(
	options: Partial<Record<keyof WidgetInputs, RegisterOptions<WidgetInputs>>>,
	errors?: FieldErrors<WidgetInputs>
): { [key in keyof WidgetInputs]?: string } {
	const { t } = useTranslation();
	if (!errors) return {};
	return Object.keys(errors || {}).reduce((acc, key) => {
		const { type, message } = errors[key as keyof WidgetInputs] as FieldError;
		const text = message && message.length ? message : getFieldError(type, key as keyof WidgetInputs);
		acc[key as keyof WidgetInputs] = t(text as any, options[key as keyof WidgetInputs]);
		return acc;
	}, {} as { [key in keyof WidgetInputs]?: string });
}

type WidgetFieldArrayError = Record<string, string>;

type BaseErrors = { [key in keyof WidgetInputs]?: string };
type ArrayFieldErrors = { [key: string]: { [key: string]: string }[] };

export function useErrorMessages(
	options: Partial<Record<keyof WidgetInputs, RegisterOptions<WidgetInputs>>>,
	errors?: FieldErrors<WidgetInputs>
): {
	baseFields: BaseErrors;
	dynamicFields: ArrayFieldErrors;
} {
	const { t } = useTranslation();
	if (!errors) return { baseFields: {}, dynamicFields: {} };

	const baseFields = Object.keys(errors || {}).reduce((acc, keyString) => {
		const key = keyString as keyof WidgetInputs;
		const fieldError = errors[key];
		if (!Array.isArray(fieldError) && key !== 'MultiFunds') {
			const text = getErrorText(fieldError as FieldError, key);
			acc[key] = t(text as any, options[key]);
		}
		return acc;
	}, {} as WidgetFieldArrayError);

	const fieldErrorsArray = errors.MultiFunds;
	const multiFunds: WidgetFieldArrayError[] = [];

	if (Array.isArray(fieldErrorsArray)) {
		for (let i = 0; i < fieldErrorsArray.length; i++) {
			const fieldErrors = fieldErrorsArray[i];
			if (!fieldErrors) {
				multiFunds.push({});
			} else {
				const errorObject: WidgetFieldArrayError = {};
				Object.keys(fieldErrors).forEach(subFieldName => {
					const error = fieldErrors[subFieldName];
					if (error) {
						const text = getErrorText(error, subFieldName as keyof WidgetInputs);
						errorObject[subFieldName] = t(text as any, options[subFieldName as keyof WidgetInputs]);
					}
				});
				multiFunds.push(errorObject);
			}
		}
	}

	return {
		baseFields,
		dynamicFields: {
			MultiFunds: multiFunds.length ? multiFunds : [],
		},
	};
}

function getErrorText(fieldError: FieldError, key: keyof WidgetInputs): string {
	const { type, message } = fieldError;
	return message && message.length ? message : getFieldError(type, key);
}
